import "@maersk-global/fonts/maeu/fonts.css";
import "@maersk-global/mds-design-tokens/maersk/light/css/design-tokens-px.css";
import "@maersk-global/mds-foundations/css/foundations.css";
import "@maersk-global/mds-components-core-button";
import "@maersk-global/mds-components-core-popover";
import "@maersk-global/mds-components-core-top-bar";
import "./visual.css";
import { MdsConfig } from "@maersk-global/mds-config";
import { installPhase4Feedback, type Phase4FeedbackContract } from "./feedback-enhancer";

export const PHASE4_VERSION = "visual-shell-actions";

export interface Phase4VisualContract {
  installed: boolean;
  version: string;
  layout: HTMLElement | null;
  topBar: HTMLElement | null;
  convertedButtonIds: string[];
  feedback?: Phase4FeedbackContract;
}

type ButtonOptions = {
  appearance: "neutral" | "primary" | "secondary" | "error";
  variant: "filled" | "outlined" | "plain";
  fit?: "small" | "medium" | "large";
  label?: string;
  icon?: string;
  hiddenlabel?: boolean;
};

type ThemeMode = "light" | "dark";

const THEME_STORAGE_KEY = "newforms-theme";
const DARK_THEME_LINK_ID = "newforms-mds-dark-theme-tokens";

function configureMdsRuntime(documentRef: Document): void {
  const isViteDevServer = Boolean(documentRef.querySelector('script[src="/@vite/client"]'));
  MdsConfig.iconsDynamicImportPath = isViteDevServer
    ? "/node_modules/"
    : new URL("./assets/node_modules/", documentRef.baseURI).pathname;
}

function copySafeAttributes(source: Element, target: HTMLElement): void {
  ["id", "name", "title", "type", "disabled", "aria-label", "aria-disabled", "data-testid"].forEach(
    (attributeName) => {
      if (source.hasAttribute(attributeName)) {
        target.setAttribute(attributeName, source.getAttribute(attributeName) || "");
      }
    },
  );
}

function getDarkThemeTokenHref(documentRef: Document): string {
  const isViteDevServer = Boolean(documentRef.querySelector('script[src="/@vite/client"]'));
  if (isViteDevServer) {
    return "/node_modules/@maersk-global/mds-design-tokens/maersk/dark/css/design-tokens-px.css";
  }
  return new URL(
    "./assets/node_modules/@maersk-global/mds-design-tokens/maersk/dark/css/design-tokens-px.css",
    documentRef.baseURI,
  ).href;
}

function readStoredTheme(documentRef: Document): ThemeMode {
  try {
    const stored = documentRef.defaultView?.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

function ensureDarkThemeStylesheet(documentRef: Document): HTMLLinkElement {
  const href = getDarkThemeTokenHref(documentRef);
  const existing = documentRef.getElementById(DARK_THEME_LINK_ID);
  if (existing instanceof HTMLLinkElement && existing.href === href) {
    return existing;
  }
  existing?.remove();

  const link = documentRef.createElement("link");
  link.id = DARK_THEME_LINK_ID;
  link.rel = "stylesheet";
  link.href = href;
  // The light token CSS is part of the visual bridge and is linked in the
  // page body. Append this override after it so the MDS dark tokens win in
  // the cascade without duplicating or hardcoding token values.
  (documentRef.body || documentRef.head).appendChild(link);
  return link;
}

function waitForStylesheet(link: HTMLLinkElement, documentRef: Document): Promise<boolean> {
  try {
    if (link.sheet) {
      return Promise.resolve(true);
    }
  } catch {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (loaded: boolean): void => {
      if (settled) {
        return;
      }
      settled = true;
      link.removeEventListener("load", onLoad);
      link.removeEventListener("error", onError);
      resolve(loaded);
    };
    const onLoad = (): void => finish(true);
    const onError = (): void => finish(false);
    link.addEventListener("load", onLoad, { once: true });
    link.addEventListener("error", onError, { once: true });
    documentRef.defaultView?.setTimeout(() => finish(Boolean(link.sheet)), 2000);
  });
}

function updateThemeButton(button: HTMLElement, theme: ThemeMode): void {
  const nextTheme = theme === "dark" ? "light" : "dark";
  const label = nextTheme === "dark" ? "Switch to dark mode" : "Switch to light mode";
  button.setAttribute("icon", nextTheme === "dark" ? "moon" : "sun");
  button.setAttribute("label", label);
  button.setAttribute("arialabel", label);
  button.setAttribute("aria-label", label);
  button.setAttribute("title", label);
  button.setAttribute("aria-pressed", String(theme === "dark"));
}

async function applyTheme(documentRef: Document, button: HTMLElement, requestedTheme: ThemeMode): Promise<ThemeMode> {
  let activeTheme: ThemeMode = requestedTheme;
  if (requestedTheme === "dark") {
    const link = ensureDarkThemeStylesheet(documentRef);
    if (!(await waitForStylesheet(link, documentRef))) {
      link.remove();
      activeTheme = "light";
    }
  } else {
    documentRef.getElementById(DARK_THEME_LINK_ID)?.remove();
  }

  documentRef.documentElement.toggleAttribute("data-theme", activeTheme === "dark");
  updateThemeButton(button, activeTheme);
  try {
    documentRef.defaultView?.localStorage.setItem(THEME_STORAGE_KEY, activeTheme);
  } catch {
    // Storage can be disabled by the browser; the current page still themes.
  }
  return activeTheme;
}

function replaceButton(source: Element, options: ButtonOptions, documentRef: Document): HTMLElement {
  if (source.localName === "mc-button") {
    return source as HTMLElement;
  }

  const target = documentRef.createElement("mc-button");
  copySafeAttributes(source, target);
  target.setAttribute("appearance", options.appearance);
  target.setAttribute("variant", options.variant);
  if (options.fit) {
    target.setAttribute("fit", options.fit);
  }
  if (options.icon) {
    target.setAttribute("icon", options.icon);
  }
  if (options.hiddenlabel) {
    target.setAttribute("hiddenlabel", "");
  }
  const legacyClasses = (source.getAttribute("class") || "")
    .split(/\s+/)
    .filter((className) => className && !["small-btn", "secondary", "danger"].includes(className));
  target.className = `${legacyClasses.join(" ")} phase4-mds-button`.trim();
  target.textContent = options.hiddenlabel ? "" : options.label || source.textContent || "";
  if (options.hiddenlabel && options.label) {
    target.setAttribute("label", options.label);
  }
  const sourceAriaLabel = source.getAttribute("aria-label");
  if (sourceAriaLabel) {
    target.setAttribute("arialabel", sourceAriaLabel);
  }
  const syncActiveState = (): void => {
    if (!target.classList.contains("lang-btn")) {
      return;
    }
    const active = target.classList.contains("active");
    (target as HTMLElement & { active?: boolean }).active = active;
    target.setAttribute("aria-pressed", String(active));
  };
  const syncLabel = (): void => {
    const label = target.textContent?.replace(/\s+/g, " ").trim() || "";
    if (label && target.getAttribute("label") !== label) {
      target.setAttribute("label", label);
    }
    syncActiveState();
  };
  syncLabel();
  source.replaceWith(target);
  if (typeof MutationObserver !== "undefined") {
    const labelObserver = new MutationObserver(syncLabel);
    labelObserver.observe(target, { attributes: true, attributeFilter: ["class"], childList: true, characterData: true, subtree: true });
  }
  return target;
}

function convertActionButtons(documentRef: Document): string[] {
  const convertedButtonIds: string[] = [];
  const buttonDefinitions: Array<[string, ButtonOptions]> = [
    ["#langPtBtn", { appearance: "neutral", variant: "plain", fit: "small", label: "PT" }],
    ["#langEnBtn", { appearance: "neutral", variant: "plain", fit: "small", label: "EN" }],
    [
      "#themeToggleBtn",
      { appearance: "neutral", variant: "plain", fit: "small", label: "Switch to dark mode", icon: "moon", hiddenlabel: true },
    ],
    ["#copyConsigneeBtn", { appearance: "neutral", variant: "outlined", fit: "small" }],
    ["#copyNotifyBtn", { appearance: "neutral", variant: "outlined", fit: "small" }],
    ["#addContainerBtn", { appearance: "neutral", variant: "outlined" }],
    ["#previewBtn", { appearance: "primary", variant: "filled" }],
    ["#sendBtn", { appearance: "secondary", variant: "filled" }],
  ];

  buttonDefinitions.forEach(([selector, options]) => {
    const source = documentRef.querySelector(selector);
    if (!source) {
      return;
    }
    const target = replaceButton(source, options, documentRef);
    if (target.id) {
      convertedButtonIds.push(target.id);
    }
  });

  const containerTemplate = documentRef.querySelector<HTMLTemplateElement>("#containerTemplate");
  containerTemplate?.content.querySelectorAll(".remove-container-btn").forEach((source) => {
    replaceButton(source, { appearance: "error", variant: "outlined", fit: "small" }, documentRef);
  });

  return convertedButtonIds;
}

function installThemeToggle(documentRef: Document): void {
  const button = documentRef.querySelector<HTMLElement>("#themeToggleBtn");
  if (!button || button.dataset.themeToggleInstalled === "true") {
    return;
  }

  button.dataset.themeToggleInstalled = "true";
  let currentTheme = readStoredTheme(documentRef);
  const commitTheme = async (requestedTheme: ThemeMode): Promise<void> => {
    currentTheme = requestedTheme;
    currentTheme = await applyTheme(documentRef, button, requestedTheme);
  };
  button.addEventListener("click", () => {
    void commitTheme(currentTheme === "dark" ? "light" : "dark");
  });
  void commitTheme(currentTheme);
}

function installRulesResponsive(documentRef: Document): void {
  const button = documentRef.querySelector<HTMLElement>("#rulesToggleBtn");
  const actions = documentRef.querySelector<HTMLElement>(".topbar-actions");
  const view = documentRef.defaultView;
  if (!button || !actions || !view || button.dataset.rulesResponsiveInstalled === "true") {
    return;
  }

  button.dataset.rulesResponsiveInstalled = "true";
  const sync = (): void => {
    const compact = view.matchMedia("(max-width: 400px)").matches;
    button.toggleAttribute("hiddenlabel", compact);
    actions.classList.toggle("phase4-topbar-actions-compact", compact);
  };
  sync();
  view.addEventListener("resize", sync);
}

function addMdsFoundationClasses(documentRef: Document): void {
  const body = documentRef.body;
  body.classList.add("mds", "phase4-page");

  const main = documentRef.querySelector<HTMLElement>("main.app-shell");
  if (!main) {
    return;
  }

  main.id = main.id || "main-content";
  main.tabIndex = -1;
  main.classList.add("mds-page", "mds-container", "phase4-page-content");

  if (!main.querySelector(":scope > .phase4-content-grid")) {
    const grid = documentRef.createElement("div");
    grid.className = "mds-grid phase4-content-grid";
    const stack = documentRef.createElement("div");
    stack.className = "mds-flex mds-flex-column mds-gap-400 phase4-content-stack";
    while (main.firstChild) {
      stack.appendChild(main.firstChild);
    }
    grid.appendChild(stack);
    main.appendChild(grid);
  }

  const hero = documentRef.querySelector<HTMLElement>(".hero");
  hero?.classList.add("mds-primary__background-color", "mds-primary__on-background-color");
  hero?.querySelector("h1")?.classList.add("mds-headline", "mds-headline--large");
  hero?.querySelector("p")?.classList.add("mds-text", "mds-text--medium-normal");

  documentRef.querySelectorAll<HTMLElement>(".card").forEach((card) => {
    card.classList.add(
      "mds-neutral--weakest__background-color",
      "mds-neutral--weak__border-color",
      "mds-flex",
      "mds-flex-column",
      "mds-flex-nowrap",
      "mds-gap-400",
    );
    card.querySelector("h2")?.classList.add("mds-headline", "mds-headline--small");
    card.querySelectorAll("h3").forEach((heading) => heading.classList.add("mds-headline", "mds-headline--x-small"));
  });

  const rulesContent = documentRef.querySelector<HTMLElement>("#rulesContent");
  rulesContent?.classList.add(
    "mds-warning--weak__background-color",
    "mds-warning--weak__on-background-color",
    "mds-warning--weak__border-color",
  );
  rulesContent?.querySelector<HTMLElement>("#rulesText")?.classList.add("mds-text", "mds-text--medium-normal");

  documentRef.querySelectorAll<HTMLElement>(".fields").forEach((fields) => {
    // Keep the legacy responsive field grid. The MDS `.mds-grid` utility is a
    // fixed 12-column layout and would collapse these auto-fit form columns.
    fields.classList.add("mds-gap-300");
  });

  documentRef.querySelectorAll<HTMLElement>(".actions, .section-head, .section-head-left").forEach((element) => {
    element.classList.add("mds-flex", "mds-flex-wrap", "mds-gap-400");
  });

  if (!documentRef.querySelector("a.phase4-skip-to")) {
    const skipLink = documentRef.createElement("a");
    skipLink.className = "mds-skip-to phase4-skip-to";
    skipLink.href = "#main-content";
    skipLink.textContent = "Skip to content";
    skipLink.addEventListener("click", (event) => {
      const target = documentRef.querySelector<HTMLElement>(skipLink.hash);
      if (!target) {
        return;
      }
      event.preventDefault();
      target.focus({ preventScroll: false });
    });
    body.insertBefore(skipLink, body.firstChild);
  }
}

function installTopBar(documentRef: Document): { layout: HTMLElement | null; topBar: HTMLElement | null } {
  const body = documentRef.body;
  const main = documentRef.querySelector<HTMLElement>("main.app-shell");
  const sourceNav = documentRef.querySelector<HTMLElement>("nav.maersk-topbar");
  if (!main || !sourceNav) {
    return { layout: null, topBar: null };
  }

  const topBar = documentRef.createElement("mc-top-bar");
  topBar.id = "phase4TopBar";
  topBar.setAttribute("product", "DRAFT SHIPMENT");
  topBar.setAttribute("productshort", "DRAFT");

  const actions = sourceNav.querySelector<HTMLElement>(".topbar-actions");
  if (actions) {
    actions.setAttribute("slot", "actions");
    actions.classList.add("mds-flex", "mds-flex-row", "mds-flex-nowrap", "mds-gap-400");
    topBar.appendChild(actions);
  }
  sourceNav.replaceWith(topBar);

  let compatibilityLabel = documentRef.querySelector<HTMLElement>(".maersk-appname");
  if (!compatibilityLabel) {
    const compatibilityLabel = documentRef.createElement("span");
    compatibilityLabel.className = "maersk-appname hidden phase4-compatibility-label";
    compatibilityLabel.textContent = "DRAFT SHIPMENT";
    body.appendChild(compatibilityLabel);
  }

  compatibilityLabel = documentRef.querySelector<HTMLElement>(".maersk-appname");
  if (compatibilityLabel) {
    const syncLanguage = (): void => {
      const product = compatibilityLabel?.textContent?.trim() || "DRAFT SHIPMENT";
      const isPortuguese = product === "RASCUNHO DE EMBARQUE";
      topBar.setAttribute("product", product);
      topBar.setAttribute("productshort", isPortuguese ? "RASCUNHO" : "DRAFT");
      const skipLink = documentRef.querySelector<HTMLElement>(".phase4-skip-to");
      if (skipLink) {
        skipLink.textContent = isPortuguese ? "Pular para o conteúdo" : "Skip to content";
      }
    };
    syncLanguage();
    if (typeof MutationObserver !== "undefined") {
      const languageObserver = new MutationObserver(syncLanguage);
      languageObserver.observe(compatibilityLabel, { childList: true, characterData: true, subtree: true });
    }
  }

  const layout = documentRef.createElement("div");
  layout.id = "phase4Layout";
  layout.className = "mds-layout mds-layout-no-side-bar";
  const pageBackground = documentRef.querySelector<HTMLElement>(".page-bg");
  body.insertBefore(layout, pageBackground || body.firstChild);
  layout.append(topBar, main);

  return { layout, topBar };
}

export function installPhase4Visuals(documentRef: Document = document): Phase4VisualContract {
  if (!documentRef?.body) {
    return { installed: false, version: PHASE4_VERSION, layout: null, topBar: null, convertedButtonIds: [] };
  }

  configureMdsRuntime(documentRef);
  const convertedButtonIds = convertActionButtons(documentRef);
  addMdsFoundationClasses(documentRef);
  const { layout, topBar } = installTopBar(documentRef);
  const feedback = installPhase4Feedback(documentRef);
  installThemeToggle(documentRef);
  installRulesResponsive(documentRef);

  return {
    installed: Boolean(layout && topBar),
    version: PHASE4_VERSION,
    layout,
    topBar,
    convertedButtonIds,
    feedback,
  };
}

const phase4Contract =
  typeof document === "undefined"
    ? { installed: false, version: PHASE4_VERSION, layout: null, topBar: null, convertedButtonIds: [] }
    : installPhase4Visuals(document);

(globalThis as typeof globalThis & { __newformsPhase4?: Phase4VisualContract }).__newformsPhase4 = phase4Contract;
